/* eslint-disable jsx-a11y/anchor-has-content -- every <a> here is a Base UI
   render-prop target for SidebarMenuButton; its visible children are merged
   in at runtime via useRender, invisible to jsx-a11y's static AST check. */
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@loop3/ui'
import {
  Building2,
  Compass,
  ExternalLink,
  KeyRound,
  Library,
  Minus,
  Plus,
  Sparkles,
} from 'lucide-react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  AppSwitcher,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  L3AppSidebar,
  LOOP3_APP_ICONS,
} from '@loop3/ui'
import { AccountSection, useIsAdmin } from '@loop3/clerk'
import { KNOWLEDGE_HUB_PATH } from '@/lib/routes'
import { useClients } from '@/hooks/useClients'
import { LupeNavSignal } from '@/components/lupe-nav-signal'
import { LUPE_PATH } from '@/hooks/useLupeThread'
import type { IconType } from '@/lib/tools'

interface InternalNav {
  title: string
  icon: IconType
  to: string
  end?: boolean
}

interface ExternalNav {
  title: string
  icon: IconType
  href: string
}

const primaryNav: InternalNav[] = [
  { title: 'Lupe', icon: Sparkles, to: LUPE_PATH, end: true },
  { title: 'Explorer', icon: Compass, to: '/explorer' },
]

const adminNav: ExternalNav[] = [
  { title: 'Secrets', icon: KeyRound, href: 'https://dashboard.doppler.com' },
]

/**
 * Clients — collapsible primary-nav item. Parent NavLink goes to /clients
 * (the directory index); the nested list is one entry per client linking
 * to /clients/:prefix. Pattern mirrors DistributionMenuItem.
 */
function ClientsMenuItem() {
  const location = useLocation()
  const clients = useClients()
  const onClientsRoute =
    location.pathname === '/clients' ||
    location.pathname.startsWith('/clients/')
  const clientsIndexActive = location.pathname === '/clients'

  return (
    <SidebarMenuItem className="group/menu-item">
      <Collapsible
        defaultOpen={onClientsRoute}
        className="group/clients-collapsible"
      >
        <NavLink to="/clients" end>
          {({ isActive }) => (
            <SidebarMenuButton isActive={isActive}>
              <Building2 className="size-4" />
              <span>Clients</span>
            </SidebarMenuButton>
          )}
        </NavLink>
        <CollapsibleTrigger
          render={
            <SidebarMenuAction
              aria-label="Toggle Clients"
              className={
                clientsIndexActive
                  ? 'cursor-pointer text-[var(--l3-violet)]'
                  : 'cursor-pointer'
              }
            />
          }
        >
          <Plus className="group-data-[open]/clients-collapsible:hidden" />
          <Minus className="hidden group-data-[open]/clients-collapsible:block" />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="ml-3.5 border-l border-sidebar-border pl-1.5">
            <SidebarMenu className="gap-0.5">
              {(clients.data ?? []).map((c) => (
                <SidebarMenuItem key={c.id}>
                  <NavLink to={`/clients/${c.prefix}`}>
                    {({ isActive }) => (
                      <SidebarMenuButton isActive={isActive}>
                        <span className="shrink-0 font-mono text-[0.65rem] uppercase tracking-[0.12em] text-[var(--l3-violet)]">
                          [{c.prefix}]
                        </span>
                        <span className="truncate">{c.name}</span>
                      </SidebarMenuButton>
                    )}
                  </NavLink>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </SidebarMenuItem>
  )
}

export function AppSidebar() {
  const { isAdmin } = useIsAdmin()

  return (
    <L3AppSidebar
      collapsible="offcanvas"
      // header (not appSwitcher): the OS switcher was pinned in SidebarHeader
      // before the shared scaffold and must not scroll with the nav.
      header={
        <AppSwitcher
          currentAppId="platform"
          icons={LOOP3_APP_ICONS}
          isAdmin={isAdmin}
        />
      }
      account={
        <SidebarMenu>
          <SidebarMenuItem>
            <AccountSection />
          </SidebarMenuItem>
        </SidebarMenu>
      }
    >
      {/* Primary nav — Lupe / Explorer / Clients / Knowledge Hub */}
      <SidebarGroup>
        <SidebarGroupContent>
          <SidebarMenu className="gap-0.5">
            {primaryNav.map((item) => (
              <SidebarMenuItem key={item.title}>
                <NavLink to={item.to} end={item.end}>
                  {({ isActive }) => (
                    <SidebarMenuButton isActive={isActive}>
                      <item.icon className="size-4" />
                      <span>{item.title}</span>
                      {item.to === LUPE_PATH && <LupeNavSignal />}
                    </SidebarMenuButton>
                  )}
                </NavLink>
              </SidebarMenuItem>
            ))}
            <ClientsMenuItem />
            <SidebarMenuItem>
              <NavLink to={KNOWLEDGE_HUB_PATH}>
                {({ isActive }) => (
                  <SidebarMenuButton isActive={isActive}>
                    <Library className="size-4" />
                    <span>Knowledge Hub</span>
                  </SidebarMenuButton>
                )}
              </NavLink>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      {isAdmin && (
        <SidebarGroup className="mt-auto">
          <SidebarGroupLabel className="eyebrow opacity-60">
            Admin
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu className="gap-0.5">
              {adminNav.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    render={
                      <a
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                      />
                    }
                  >
                    <item.icon className="size-4" />
                    <span>{item.title}</span>
                    <ExternalLink className="ml-auto size-3 opacity-60" />
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      )}
    </L3AppSidebar>
  )
}
